import { useMemo, useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isSameMonth,
} from "date-fns";
import { enUS, es } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  requestTypeStatusColor,
  formatMDY as formatMDYHelper,
  formatMDYWithTime,
  getStatusColor,
} from "@utils/ptoHelpers";
import { Toaster, toast } from "sonner";
import { fetchPtoByMonth, createPtoRequests } from "@service/ptoService";
import { useAuth } from "@components/context/AuthContext";
import { PTOVersionHistoryDrawer } from "@components/user_pto/PTOVersionHistoryDrawer";
import { PTORequestModal } from "@components/user_pto/PTORequestModal";
import { PTOListMobile } from "@components/user_pto/PTOListMobile";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

export default function PtoPage() {
  const { t, i18n } = useTranslation();
  const { user, getInfo, loading } = useAuth();
  const queryClient = useQueryClient();

  const [user_name, setUser_name] = useState("");
  const [user_id, setUser_id] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [description, setDescription] = useState("");
  const [selection, setSelection] = useState<Record<string, string>>({}); // { 'yyyy-MM-dd': 'FULL'|'AM'|'PM' }
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "calendar">("calendar");
  const [moreForDay, setMoreForDay] = useState<string | null>(null);
  const [showSuccessAnim, setShowSuccessAnim] = useState(false);
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [activeRequest, setActiveRequest] = useState<any>(null);
  const requestType = "vacation";
  const breakpoint = 768; // Define the breakpoint for mobile view

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  const [searchVal, setSearchVal] = useState("");

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  useEffect(() => {
    async function fetchInfo() {
      const result = await getInfo();
      if (result.roles === undefined || result.userType === undefined) {
        return;
      }
      setUser_name(result.user_name);
      setUser_id(result.user_id);
    }
    fetchInfo();
  }, [getInfo, user]);

  const {
    data: ptoData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["ptoRequests"],
    queryFn: () => fetchPtoByMonth(),
    enabled: !loading,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  const requests = useMemo(() => ptoData?.requests ?? [], [ptoData?.requests]);
  const blockedDays = useMemo(
    () => ptoData?.blockedDays ?? [],
    [ptoData?.blockedDays]
  );
  const createMutation = useMutation({
    mutationFn: async ({
      selections,
      description,
      user_id,
    }: {
      selections: Array<{ date: string; part: string }>;
      description?: string | null;
      user_id: number;
    }) => {
      return await createPtoRequests({
        selections,
        description: description || "",
        user_id,
        request_type: requestType,
      });
    },
    onSuccess: () => {
      toast.success(t("pto.requestSubmitted"));
      setShowSuccessAnim(true);
      queryClient.invalidateQueries({ queryKey: ["ptoRequests"] });
      setTimeout(() => {
        setSelection({});
        setDescription("");
        setIsOpen(false);
        setShowSuccessAnim(false);
      }, 800);
    },
    onError: (err: any) => {
      console.error(err);
      const message =
        err?.response?.data?.error || err.message || t("pto.submitError");
      setSubmitError(message);
      toast.error(message);
    },
  });

  const onFinalize = useCallback(() => {
    setSubmitError(null);
    const selections = Object.entries(selection).map(([date, part]) => ({
      date,
      part,
    }));
    if (selections.length === 0) {
      setSubmitError(t("pto.selectAtLeastOneDay"));
      toast.message(t("pto.selectAtLeastOneDay"));
      return;
    }
    createMutation.mutate({ selections, description, user_id });
  }, [selection, description, createMutation, user_id, t]);

  const handlePrevMonth = () => setCurrentMonth((d) => addMonths(d, -1));
  const handleNextMonth = () => setCurrentMonth((d) => addMonths(d, 1));

  const monthMatrix = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    const days = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }, [currentMonth]);

  const toggle = (dateStr: string) => {
    setSelection((prev) => {
      const current = prev[dateStr];
      if (current === "FULL") {
        // const { [dateStr]: _, ...rest } = prev;
        return { ...prev, [dateStr]: "AM" };
      }
      if (current === "AM") {
        const { [dateStr]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [dateStr]: "FULL" };
    });
  };

  const blockedMap = useMemo(() => {
    const map: Record<string, { AM: boolean; PM: boolean; FULL: boolean }> = {};
    for (const b of blockedDays) {
      const dateStr = b.date || b.date_str || b.dateStr;
      const part = String(b.part || "FULL").toUpperCase();
      if (!dateStr) continue;
      if (!map[dateStr]) {
        map[dateStr] = { AM: false, PM: false, FULL: false };
      }
      if (part === "FULL") {
        map[dateStr] = { AM: true, PM: true, FULL: true };
      } else if (part === "AM" || part === "PM") {
        map[dateStr][part as "AM" | "PM"] = true;
        if (map[dateStr].AM && map[dateStr].PM) {
          map[dateStr].FULL = true;
        }
      }
    }
    return map;
  }, [blockedDays]);

  const dayEvents = useMemo(() => {
    const map = new Map<string, any[]>();
    (requests || []).forEach((r: any) => {
      if (!r.start_date || !r.end_date) return;
      const startStr = String(r.start_date).slice(0, 10);
      const endStr = String(r.end_date).slice(0, 10);

      if (!startStr || !endStr) return;
      const start = new Date(`${startStr}T00:00:00`);
      const end = new Date(`${endStr}T00:00:00`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = format(d, "yyyy-MM-dd");
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(r);
      }
    });
    return map;
  }, [requests]);

  const formatMDY = (value: any) => {
    return formatMDYHelper(value);
  };

  const formatMDYT = (value: any) => {
    return formatMDYWithTime(value);
  };

  const handleRequestClick = (request: any) => {
    setActiveRequest(request);
    setLogsDrawerOpen(true);
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: t("pto.requestedDate"),
        field: "request_date",
        filter: true,
        width: 180,
        sort: "desc",
        valueFormatter: (params: any) => formatMDYT(params.value) || "-",
      },
      {
        headerName: t("pto.startDate"),
        field: "start_date",
        filter: true,
        width: 140,
        valueFormatter: (params: any) => formatMDY(params.value) || "-",
      },
      {
        headerName: t("pto.endDate"),
        field: "end_date",
        width: 140,
        filter: true,
        valueFormatter: (params: any) => formatMDY(params.value) || "-",
      },
      {
        headerName: t("pto.hours"),
        field: "hours_requested",
        width: 100,
        filter: true,
      },
      {
        headerName: t("pto.description"),
        field: "description",
        filter: true,
        flex: 1,
        minWidth: 150,
        valueFormatter: (params: any) => params.value || "—",
        tooltipField: "description",
      },
      {
        headerName: t("pto.status"),
        field: "status",
        filter: true,
        width: 140,
        cellRenderer: (params: any) => {
          const status = params.value;
          const statusKey = status?.toLowerCase() || "";
          const translatedStatus = t(`pto.statuses.${statusKey}`, {
            defaultValue: status,
          });
          return (
            <div
              className={`tw-capitalize ${getStatusColor(
                status,
                params.data.request_type
              )}`}
            >
              {translatedStatus}
            </div>
          );
        },
      },
      {
        headerName: t("pto.requestType"),
        field: "request_type",
        filter: true,
        width: 140,
        valueFormatter: (params: any) => params.value || "—",
        cellClass: "tw-capitalize",
      },
      {
        headerName: t("pto.adminNote"),
        field: "status_reason",
        filter: true,
        width: 180,
        valueFormatter: (params: any) => params.value || "—",
        tooltipField: "status_reason",
      },
    ],
    [t]
  );

  return (
    <div className="tw-p-4 md:tw-p-6">
      <Toaster position="top-right" richColors />
      <div className="tw-flex tw-items-center tw-justify-between">
        <h1 className="tw-text-xl md:tw-text-2xl tw-font-semibold">
          {user_name ? (
            <>
              {t("pto.title")} -{" "}
              <span className="tw-font-normal">{user_name}</span>
            </>
          ) : (
            t("pto.title")
          )}
        </h1>
        <button
          onClick={() => setIsOpen(true)}
          className="tw-px-3 tw-py-2 tw-bg-[#111827] tw-text-white tw-rounded-md hover:tw-bg-black"
        >
          {t("pto.addVacationPto")}
        </button>
      </div>

      <div className="tw-mt-4 tw-bg-white tw-rounded-lg tw-border tw-border-[#E5E7EB] tw-overflow-hidden">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border-b">
          <div className="tw-text-sm tw-font-medium">{t("pto.myPto")}</div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              className={`tw-px-3 tw-py-1.5 tw-rounded-md tw-border ${
                viewMode === "table"
                  ? "tw-bg-blue-600 tw-text-white tw-border-blue-600"
                  : "tw-bg-white tw-text-gray-700 tw-border-gray-300"
              }`}
              onClick={() => setViewMode("table")}
            >
              {t("pto.tableView")}
            </button>
            <button
              className={`tw-px-3 tw-py-1.5 tw-rounded-md tw-border ${
                viewMode === "calendar"
                  ? "tw-bg-blue-600 tw-text-white tw-border-blue-600"
                  : "tw-bg-white tw-text-gray-700 tw-border-gray-300"
              }`}
              onClick={() => setViewMode("calendar")}
            >
              {t("pto.calendarView")}
            </button>
          </div>
        </div>

        {viewMode === "table" && isMobile && (
          <div className="tw-px-3 tw-pt-3">
            <input
              type="text"
              placeholder={t("pto.searchPlaceholder") || "Search..."}
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            />
          </div>
        )}

        {viewMode === "table" ? (
          !isMobile ? (
            <div
              className="ag-theme-balham"
              style={{ height: 600, width: "100%" }}
              onContextMenu={handleContextMenu}
            >
              <AgGridReact
                rowData={isLoading ? undefined : requests}
                columnDefs={columnDefs}
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                }}
                animateRows={true}
                rowSelection="single"
                onCellContextMenu={handleCellContextMenu}
                onRowClicked={(params) => handleRequestClick(params.data)}
                overlayLoadingTemplate={
                  '<span class="ag-overlay-loading-center">' +
                  t("pto.loading") +
                  "</span>"
                }
                overlayNoRowsTemplate={
                  '<span class="ag-overlay-no-rows-center">' +
                  t("pto.noRequests") +
                  "</span>"
                }
              />
              {ContextMenuComponent}
            </div>
          ) : (
            <div className="tw-p-3">
              <PTOListMobile
                requests={requests}
                searchVal={searchVal}
                onRequestClick={handleRequestClick}
              />
            </div>
          )
        ) : (
          <div className="tw-p-3">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <button
                onClick={handlePrevMonth}
                className="tw-p-2 tw-border tw-rounded-md"
                aria-label="Previous month"
              >
                <ChevronLeft className="tw-w-4 tw-h-4" />
              </button>
              <div className="tw-font-medium">
                {format(currentMonth, "MMMM yyyy", {
                  locale: i18n.language === "es" ? es : enUS,
                })}
              </div>
              <button
                onClick={handleNextMonth}
                className="tw-p-2 tw-border tw-rounded-md"
                aria-label="Next month"
              >
                <ChevronRight className="tw-w-4 tw-h-4" />
              </button>
            </div>

            <div className="tw-grid tw-grid-cols-7 tw-text-center tw-text-xs tw-text-[#6B7280]">
              {[
                t("pto.sun"),
                t("pto.mon"),
                t("pto.tue"),
                t("pto.wed"),
                t("pto.thu"),
                t("pto.fri"),
                t("pto.sat"),
              ].map((d) => (
                <div key={d} className="tw-py-1">
                  {d}
                </div>
              ))}
            </div>

            <div
              className="tw-grid tw-grid-cols-7 tw-gap-1"
              onClick={() => {
                if (moreForDay) setMoreForDay(null);
              }}
            >
              {isLoading && (
                <div className="tw-col-span-7 tw-p-3 tw-text-gray-500">
                  Loading…
                </div>
              )}
              {!isLoading && error && (
                <div className="tw-col-span-7 tw-p-3 tw-text-red-600">
                  Failed to load PTO requests
                </div>
              )}
              {!isLoading &&
                !error &&
                monthMatrix.map((week, wi) => (
                  <div key={wi} className="tw-contents">
                    {week.map((day, di) => {
                      const dateStr = format(day, "yyyy-MM-dd");
                      const inMonth = isSameMonth(day, currentMonth);
                      const dayList = dayEvents.get(dateStr) || [];

                      return (
                        <div
                          key={di}
                          className={`tw-min-h-[84px] tw-border tw-rounded-md tw-relative ${
                            inMonth ? "tw-bg-white" : "tw-bg-[#F3F4F6]"
                          }`}
                        >
                          <div className="tw-text-[11px] tw-text-gray-600 tw-p-1">
                            {format(day, "d")}
                          </div>
                          <div className="tw-mt-1 tw-space-y-1 tw-relative">
                            {dayList.slice(0, 3).map((r: any) => {
                              const startStr = String(r.start_date).slice(
                                0,
                                10
                              );
                              const endStr = String(r.end_date).slice(0, 10);
                              const startsHere = startStr === dateStr;
                              const endsHere = endStr === dateStr;
                              const { bg, text } = requestTypeStatusColor(
                                r.status,
                                r.request_type
                              );
                              const mainText = r.description
                                ? r.description
                                : "PTO";
                              const status = (r.status || "")
                                .toString()
                                .toLowerCase();
                              const statusText = t(`pto.statuses.${status}`, {
                                defaultValue: r.status,
                              });
                              const hoursText =
                                typeof r.hours_requested === "number"
                                  ? ` • ${r.hours_requested}h`
                                  : "";
                              const requestTypeText = r.request_type
                                ? ` (${r.request_type})`
                                : "";
                              const label = `${mainText}${requestTypeText} - ${statusText}${
                                endsHere ? hoursText : ""
                              }`;
                              return (
                                <div
                                  key={`${r.id}-${dateStr}`}
                                  className={`${bg} ${text} tw-text-[10px] tw-leading-tight tw-px-1 tw-py-0.5 tw-cursor-pointer hover:tw-opacity-80 ${
                                    startsHere && endsHere
                                      ? "tw-rounded tw-m-2"
                                      : startsHere
                                      ? "tw-rounded-l tw-ml-2"
                                      : endsHere
                                      ? "tw-rounded-r tw-mr-2"
                                      : ""
                                  } tw-overflow-hidden tw-whitespace-nowrap`}
                                  title={`${r.description || "PTO"} • ${
                                    r.status
                                  } - Click to view logs`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestClick(r);
                                  }}
                                >
                                  {label}
                                </div>
                              );
                            })}
                            {dayList.length > 3 && (
                              <div
                                className="tw-text-[10px] tw-text-gray-600 tw-cursor-pointer tw-underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMoreForDay(dateStr);
                                }}
                              >
                                +{dayList.length - 3} more
                              </div>
                            )}

                            {moreForDay === dateStr && (
                              <div
                                className="tw-absolute tw-z-20 tw-right-0 tw-top-5 tw-w-[220px] tw-max-h-[220px] tw-overflow-auto tw-border tw-border-gray-200 tw-rounded-md tw-bg-white tw-shadow-lg tw-p-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1 tw-text-xs tw-text-gray-600">
                                  <span>{formatMDY(dateStr)}</span>
                                  <button
                                    className="tw-px-1 tw-py-0.5 tw-border tw-rounded tw-text-[10px]"
                                    onClick={() => setMoreForDay(null)}
                                  >
                                    Close
                                  </button>
                                </div>
                                <div className="tw-space-y-1">
                                  {dayList.map((r: any) => {
                                    const { bg, text } = requestTypeStatusColor(
                                      r.status,
                                      r.request_type
                                    );
                                    const label = `${
                                      r.description ? r.description : "PTO"
                                    } - ${(r.status || "").toString()}`;
                                    return (
                                      <div
                                        key={`pop-${r.id}-${dateStr}`}
                                        className={`${bg} ${text} tw-text-[11px] tw-px-1 tw-py-0.5 tw-rounded tw-cursor-pointer hover:tw-opacity-80`}
                                        title={`${r.description || "PTO"} • ${
                                          r.status
                                        } - Click to view logs`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setMoreForDay(null);
                                          handleRequestClick(r);
                                        }}
                                      >
                                        {label}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      <PTORequestModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        currentMonth={currentMonth}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        monthMatrix={monthMatrix}
        selection={selection}
        onToggle={toggle}
        blockedMap={blockedMap}
        description={description}
        onDescriptionChange={setDescription}
        submitError={submitError}
        onFinalize={onFinalize}
        isPending={createMutation.isPending}
        showSuccessAnim={showSuccessAnim}
        requestType={requestType}
        userName={user_name}
      />

      {/* Logs Drawer */}
      <PTOVersionHistoryDrawer
        isOpen={logsDrawerOpen}
        request={activeRequest}
        onClose={() => {
          setLogsDrawerOpen(false);
          setActiveRequest(null);
        }}
      />

      <style>{`
        @keyframes success-pop {
          0% { transform: scale(0.9); opacity: 0.7; }
          50% { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .pulse-check { animation: success-pop 0.5s ease-out; }
      `}</style>
    </div>
  );
}
